/* =============================================================================
   DAILE Mono — product page behaviour (2026-10-05). Loaded by layout/mono.liquid on product templates.
   No dependencies; assets/mn.js (theme, reveals, rails, reels, autoplay) runs alongside.

   1. Buy box: set choice (Mini 300/500 · Classic · Signature), glue add-on, prices, ?variant= / ?set=,
      cart contract for assets/daile-cart.js (form._daileMvpSelection — copied from assets/daile-mvp.js),
      custom-puzzle customizer links (?set=…[&glue=1]); window.DAILE_MVP + 'daile-mvp:tier' event kept.
      R7: the glue row is shown only for Mini; "What's included" panes / set name follow the chosen set.
   2. Gallery: #set: filter, swipe track (scroll-snap), arrows, dots, counter, thumbnails
   3. Lightbox: gallery zoom + set-photo viewer (one <dialog>)
   4. Sticky bars (IntersectionObserver only — no scroll handlers)
   5. NFC dialog (film loads on open)
   6. "Remind me later" (Klaviyo client API — same as daile-mvp.js initRemind)
   7. Custom puzzle: photo upload in "How it works" → IndexedDB → customizer (as daile-pdp.js initPdpUpload)
   8. "What's in the box" follows the chosen set
   9. Related products (recommendations API, loaded near the viewport into a height-reserving shell)
   10. Preview links (?view=mono) + cookie banner offset for the phone bar
   11. R8 gift postcard (art PDPs): toggle, live front / back preview, message, one cart/add.js request
       {puzzle _line_id, glue kit, post-card {_parent_line, 'Card message'}}; shipping line follows the total
   12. R9 shipping line "Change" → window.mnLocale.open() (shared country / language modal); select = fallback
   ============================================================================= */
(() => {
  'use strict';

  const d = document;
  const html = d.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasIO = 'IntersectionObserver' in window;
  const decoder = d.createElement('textarea');
  const decode = (s) => { decoder.innerHTML = s == null ? '' : String(s); return decoder.value; };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const isRtl = () => (html.getAttribute('dir') || '').toLowerCase() === 'rtl';
  const shopRoot = (() => {
    let r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (r.charAt(r.length - 1) !== '/') r += '/';
    return r;
  })();

  const root = d.querySelector('[data-mn-pdp]');
  let cfg = {};
  if (root) {
    try { cfg = JSON.parse(root.querySelector('[data-mn-pdp-config]').textContent); } catch (e) { cfg = {}; }
  }
  const L = (cfg.labels) || {};
  const label = (path, fallback) => {
    const v = path.split('.').reduce((o, k) => (o && o[k] != null ? o[k] : null), L);
    return v == null ? fallback || '' : decode(v);
  };

  /* 3. Lightbox (used by the gallery and by the set cards) ================= */

  const Lightbox = (() => {
    const dlg = d.querySelector('[data-mn-lb]');
    if (!dlg || typeof dlg.showModal !== 'function') return null;
    const stage = dlg.querySelector('[data-mn-lb-stage]');
    const img = dlg.querySelector('[data-mn-lb-img]');
    const tag = dlg.querySelector('[data-mn-lb-tag]');
    const titleEl = dlg.querySelector('[data-mn-lb-title]');
    const countEl = dlg.querySelector('[data-mn-lb-count]');
    const capEl = dlg.querySelector('[data-mn-lb-cap]');
    const prev = dlg.querySelector('[data-mn-lb-prev]');
    const next = dlg.querySelector('[data-mn-lb-next]');
    const baseLabel = dlg.getAttribute('aria-label') || '';
    let list = [];
    let index = 0;
    let opener = null;
    let zoomed = false;
    let token = 0;

    const pad = (n) => (n < 10 ? `0${n}` : String(n));
    const setZoom = (on, x, y) => {
      zoomed = on;
      img.classList.toggle('is-zoomed', on);
      if (on && x != null) {
        const r = img.getBoundingClientRect();
        img.style.transformOrigin = `${((x - r.left) / r.width) * 100}% ${((y - r.top) / r.height) * 100}%`;
      }
      if (!on) img.style.transformOrigin = '';
    };
    const show = (i) => {
      if (!list.length) return;
      index = (i + list.length) % list.length;
      const m = list[index];
      setZoom(false);
      const t = ++token;
      img.classList.add('is-loading');
      const done = () => { if (t === token) img.classList.remove('is-loading'); };
      img.onload = done;
      img.onerror = done;
      img.removeAttribute('srcset');
      img.src = m.src;
      if (m.w && m.h) { img.width = m.w; img.height = m.h; }
      img.alt = m.alt || '';
      if (img.complete && img.naturalWidth) done();
      tag.hidden = !/glue/i.test(m.alt || '');
      capEl.textContent = m.alt || '';
      const many = list.length > 1;
      countEl.textContent = many ? `${pad(index + 1)} / ${pad(list.length)}` : '';
      prev.hidden = !many;
      next.hidden = !many;
      [index + 1, index - 1].forEach((k) => {
        const n = list[(k + list.length) % list.length];
        if (n && n !== m) { const pre = new Image(); pre.src = n.src; }
      });
    };
    const go = (step) => { if (list.length > 1) show(index + step); };
    const close = () => { if (dlg.open) dlg.close(); };

    // Logical buttons: «prev» (inline-start side, mirrored in RTL) = previous photo.
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    dlg.querySelector('[data-mn-lb-close]').addEventListener('click', close);
    dlg.addEventListener('close', () => {
      html.classList.remove('mn-lock');
      token++;
      img.removeAttribute('src');
      if (opener && opener.focus) { try { opener.focus({ preventScroll: true }); } catch (e) { /* */ } }
    });
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        let fwd = e.key === 'ArrowRight';
        if (isRtl()) fwd = !fwd;
        go(fwd ? 1 : -1);
      }
    });

    // Tap / click: zoom in at that point, again to zoom out. Drag while zoomed pans; swipe when not zoomed.
    let x0 = null; let y0 = 0; let moved = false; let pid = null;
    stage.addEventListener('pointerdown', (e) => {
      if (e.button > 0 || e.target.closest('button')) return;
      x0 = e.clientX; y0 = e.clientY; moved = false; pid = e.pointerId;
      if (zoomed) stage.classList.add('is-panning');
    });
    stage.addEventListener('pointermove', (e) => {
      if (zoomed && (x0 !== null || e.pointerType === 'mouse')) {
        const r = stage.getBoundingClientRect();
        img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
        if (x0 !== null && Math.abs(e.clientX - x0) + Math.abs(e.clientY - y0) > 6) moved = true;
        return;
      }
      if (x0 !== null && e.pointerId === pid && Math.abs(e.clientX - x0) > 8) moved = true;
    });
    const end = (e) => {
      if (x0 === null || (e && e.pointerId !== pid)) return;
      const dx = e.clientX - x0;
      const wasZoomed = zoomed;
      stage.classList.remove('is-panning');
      x0 = null;
      if (!wasZoomed && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(e.clientY - y0)) {
        let fwd = dx < 0;
        if (isRtl()) fwd = !fwd;
        go(fwd ? 1 : -1);
        return;
      }
      if (moved && wasZoomed) return;
      if (e.target === img || e.target === stage) {
        if (wasZoomed) setZoom(false);
        else if (e.target === img) setZoom(true, e.clientX, e.clientY);
        else close();
      }
    };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', () => { x0 = null; stage.classList.remove('is-panning'); });

    return {
      open(items, start, opts = {}) {
        list = items.filter((m) => m && m.src);
        if (!list.length) return;
        opener = opts.opener || d.activeElement;
        titleEl.textContent = opts.title || '';
        dlg.setAttribute('aria-label', opts.title ? `${opts.title}` : baseLabel);
        show(start || 0);
        html.classList.add('mn-lock');
        dlg.showModal();
      },
    };
  })();

  /* 2. Gallery ============================================================= */

  const Gallery = (() => {
    const gal = d.querySelector('[data-mn-gal]');
    if (!gal) return null;
    const track = gal.querySelector('[data-mn-gal-track]');
    const slides = Array.from(track.querySelectorAll('[data-mn-slide]'));
    const thumbs = Array.from(gal.querySelectorAll('[data-mn-thumb]'));
    const dotsBox = gal.querySelector('[data-mn-gal-dots]');
    const curEl = gal.querySelector('[data-mn-gal-cur]');
    const totalEl = gal.querySelector('[data-mn-gal-total]');
    const prevBtn = gal.querySelector('[data-mn-gal-prev]');
    const nextBtn = gal.querySelector('[data-mn-gal-next]');
    const zoomBtn = gal.querySelector('[data-mn-gal-zoom]');
    if (thumbs.length < 2) gal.classList.add('mn-pdp-gal--single');
    let visible = slides.filter((s) => !s.hidden);
    let current = 0;
    let dots = [];

    const ratio = new Map();
    const render = () => {
      if (curEl) curEl.textContent = String(current + 1);
      if (totalEl) totalEl.textContent = String(visible.length);
      dots.forEach((dot, k) => dot.classList.toggle('is-active', k === current));
      if (prevBtn) prevBtn.disabled = current <= 0;
      if (nextBtn) nextBtn.disabled = current >= visible.length - 1;
      const active = visible[current];
      // One tab stop in the track: the visible photo's zoom button.
      slides.forEach((s) => { const hit = s.querySelector('[data-mn-zoom]'); if (hit) hit.tabIndex = s === active ? 0 : -1; });
      thumbs.forEach((t, k) => {
        const b = t.firstElementChild;
        if (b) b.setAttribute('aria-current', String(slides[k] === active));
      });
      // Keep the active thumbnail in view — inside the thumbnail column only, never the page.
      const k = slides.indexOf(active);
      const t = thumbs[k];
      const box = t && t.parentElement;
      if (t && box && box.scrollHeight > box.clientHeight) {
        const tr = t.getBoundingClientRect();
        const br = box.getBoundingClientRect();
        if (tr.top < br.top) box.scrollBy({ top: tr.top - br.top - 8, behavior: 'smooth' });
        else if (tr.bottom > br.bottom) box.scrollBy({ top: tr.bottom - br.bottom + 8, behavior: 'smooth' });
      }
    };
    const buildDots = () => {
      if (!dotsBox) return;
      dotsBox.textContent = '';
      dots = visible.length > 1 ? visible.map(() => dotsBox.appendChild(d.createElement('span'))) : [];
    };
    // Scroll the TRACK (never the page) so that slide i is in place.
    const scrollToIndex = (i, instant) => {
      const s = visible[i];
      if (!s) return;
      const sr = s.getBoundingClientRect();
      const tr = track.getBoundingClientRect();
      const delta = isRtl() ? sr.right - tr.right : sr.left - tr.left;
      if (Math.abs(delta) < 1) return;
      track.scrollBy({ left: delta, behavior: instant || reduce.matches ? 'instant' : 'smooth' });
    };
    const go = (i, instant) => {
      current = Math.max(0, Math.min(visible.length - 1, i));
      scrollToIndex(current, instant);
      render();
    };

    if (hasIO) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => ratio.set(en.target, en.isIntersecting ? en.intersectionRatio : 0));
        let best = -1; let bestR = 0;
        visible.forEach((s, k) => { const r = ratio.get(s) || 0; if (r > bestR + 0.01) { bestR = r; best = k; } });
        if (best > -1 && bestR >= 0.5 && best !== current) { current = best; render(); }
      }, { root: track, threshold: [0, 0.5, 0.75, 1] });
      slides.forEach((s) => io.observe(s));
    }

    if (prevBtn) prevBtn.addEventListener('click', () => go(current - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => go(current + 1));
    thumbs.forEach((t, k) => {
      const b = t.firstElementChild;
      if (b) b.addEventListener('click', () => { const i = visible.indexOf(slides[k]); if (i > -1) go(i); });
    });
    track.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      let fwd = e.key === 'ArrowRight';
      if (isRtl()) fwd = !fwd;
      go(current + (fwd ? 1 : -1));
    });

    const itemsForLightbox = () => visible
      .filter((s) => s.getAttribute('data-full'))
      .map((s) => ({ src: s.getAttribute('data-full'), alt: s.getAttribute('data-alt') || '', w: +s.getAttribute('data-w') || 0, h: +s.getAttribute('data-h') || 0, el: s }));
    const openZoom = (from) => {
      if (!Lightbox) return;
      const items = itemsForLightbox();
      const k = items.findIndex((m) => m.el === visible[current]);
      Lightbox.open(items, Math.max(0, k), { opener: from, title: '' });
    };
    track.addEventListener('click', (e) => {
      const hit = e.target.closest('[data-mn-zoom]');
      if (!hit) return;
      const s = hit.closest('[data-mn-slide]');
      const i = visible.indexOf(s);
      if (i > -1) current = i;
      openZoom(hit);
    });
    if (zoomBtn) zoomBtn.addEventListener('click', () => openZoom(zoomBtn));

    let lastTier = null;
    const filter = (keys, tier) => {
      const shown = visible[current];
      slides.forEach((s, k) => {
        const tags = s.getAttribute('data-mn-tags');
        const show = !tags || tags.split(',').some((t) => keys.indexOf(t.trim()) !== -1);
        s.hidden = !show;
        if (thumbs[k]) thumbs[k].hidden = !show;
      });
      visible = slides.filter((s) => !s.hidden);
      buildDots();
      const changed = lastTier !== null && tier !== lastTier;
      const first = lastTier === null;
      lastTier = tier;
      if (first) { render(); return; }
      if (changed) {
        // New set: from the artwork, glide to the first photo of the set (track only, the page stays put).
        current = 0;
        scrollToIndex(0, true);
        render();
        if (visible.length > 1) setTimeout(() => go(1), reduce.matches ? 0 : 60);
        return;
      }
      // Same set (Mini 300 ↔ 500): stay on the same photo if it is still there.
      const keep = visible.indexOf(shown);
      go(keep > -1 ? keep : Math.min(current, visible.length - 1), true);
    };

    buildDots();
    render();
    return { filter };
  })();

  /* 1. Buy box ============================================================= */

  const Buy = (() => {
    if (!root || !cfg.hasTiers) return null;
    const isCustom = cfg.mode === 'custom';
    const V = cfg.variants || {};
    const glue = cfg.glue || {};
    const cards = Array.from(root.querySelectorAll('[data-mn-set]'));
    const glueInput = root.querySelector('[data-mn-glue]');
    const form = root.querySelector('form[data-mn-form]');
    const st = window.DAILE_MVP = window.DAILE_MVP || {};
    st.tier = root.getAttribute('data-tier') || (isCustom ? 'classic' : 'mini');
    st.pieces = parseInt(root.getAttribute('data-pieces'), 10) === 500 ? 500 : 300;
    st.glue = !!(glueInput && glueInput.checked);
    const card = cfg.card || null;
    const cardInput = root.querySelector('[data-mn-pc-toggle]');
    st.card = !!(card && cardInput && cardInput.checked);
    st.cardText = '';

    // ?set= alias (?variant= is already applied by Liquid through selected_variant).
    try {
      const q = new URLSearchParams(window.location.search);
      const alias = (q.get('set') || '').toLowerCase().replace(/[\s_-]/g, '');
      const map = { mini300: ['mini', 300], 300: ['mini', 300], mini500: ['mini', 500], 500: ['mini', 500], classic: ['classic'], 1000: ['classic'], signature: ['signature'] };
      if (map[alias] && !q.get('variant')) {
        st.tier = map[alias][0];
        if (map[alias][1]) st.pieces = map[alias][1];
      }
    } catch (e) { /* old browser */ }

    const key = () => (st.tier === 'mini' ? `mini-${st.pieces}` : st.tier);
    const piecesOf = () => (st.tier === 'mini' ? st.pieces : 1000);
    const shortName = () => label(`short.${st.tier}`, st.tier.toUpperCase());
    const money = () => {
      const v = V[key()] || {};
      const g = st.tier === 'mini' && st.glue;
      if (st.card) return decode(g ? v.glueCardMoney || v.glueMoney : v.cardMoney || v.money);
      return decode(g ? v.glueMoney || v.money : v.money);
    };
    // Total in cents (presentment currency) for the shipping line's free-delivery threshold.
    const cents = () => {
      const v = V[key()] || {};
      return (v.cents || 0) + (st.tier === 'mini' && st.glue ? glue.cents || 0 : 0) + (st.card && card ? card.cents || 0 : 0);
    };
    const setText = (sel, fn) => d.querySelectorAll(sel).forEach((el) => { fn(el); });
    const sumHtml = (withDaile) => `<span>${withDaile ? 'DAILE ' : ''}${esc(shortName())}</span><span class="mn-pdp__sep" aria-hidden="true">|</span><span><bdi>${piecesOf()}</bdi> ${esc(label('pieces', 'pieces'))}</span>`;

    const syncForm = () => {
      const v = V[key()] || {};
      const withGlue = st.tier === 'mini' && st.glue;
      const glueOk = !!glue.id && !!glue.available;
      st.variantId = v.id || null;
      st.available = !!v.id && !!v.available && (!withGlue || glueOk);
      st.priceText = money();
      st.czSet = st.tier === 'mini' ? `mini${st.pieces}` : st.tier;
      st.czGlue = withGlue;
      // Customizer links (custom-puzzle): ?set=mini300|mini500|classic|signature[&glue=1]
      if (cfg.czUrl) {
        d.querySelectorAll('[data-mn-cz-link]').forEach((a) => a.setAttribute('href', `${cfg.czUrl}?set=${st.czSet}${st.czGlue ? '&glue=1' : ''}`));
      }
      const msg = root.querySelector('[data-mn-buy-msg]');
      if (msg) { msg.hidden = st.available; msg.textContent = st.available ? '' : label('unavailable'); }
      if (!form) return;
      // Cart contract read by assets/daile-cart.js (handleAdd): same fields as assets/daile-mvp.js.
      form._daileMvpSelection = {
        variantId: st.variantId,
        available: st.available,
        addonVariantId: withGlue && glueOk ? glue.id : null,
      };
      let input = form.querySelector('input[name="id"]');
      if (!input) { input = d.createElement('input'); input.type = 'hidden'; input.name = 'id'; form.appendChild(input); }
      const nextId = st.available ? String(st.variantId) : '';
      const changed = input.value !== nextId || input.disabled === st.available;
      input.value = nextId;
      input.disabled = !st.available;
      const buttons = Array.from(form.querySelectorAll('button[type="submit"]')).concat(Array.from(d.querySelectorAll('[data-mn-bar-add]')));
      buttons.forEach((b) => { b.disabled = !st.available; b.setAttribute('aria-disabled', String(!st.available)); });
      const lab = form.querySelector('[data-mn-atc-label]');
      if (lab) lab.textContent = st.available ? label('add', 'Add to cart') : label('soldOut', 'Sold out');
      d.querySelectorAll('[data-mn-bar-add]').forEach((b) => { b.textContent = st.available ? label('add', 'Add to cart') : label('soldOut', 'Sold out'); });
      // Shop Pay buys one variant only: hidden with the glue add-on / postcard or when unavailable (as before).
      const pay = form.querySelector('[data-mn-pay]');
      if (pay) pay.hidden = !st.available || withGlue || st.card;
      if (changed) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
        if (window.Shopify && window.Shopify.PaymentButton && window.Shopify.PaymentButton.init) {
          try { window.Shopify.PaymentButton.init(); } catch (e) { /* */ }
        }
      }
    };

    const render = () => {
      if (st.tier === 'mini' && st.pieces !== 300 && st.pieces !== 500) st.pieces = 300;
      cards.forEach((c) => {
        const on = c.getAttribute('data-mn-set') === st.tier;
        c.classList.toggle('is-on', on);
        const r = c.querySelector('.mn-pdp-set__radio');
        if (r && r.checked !== on) r.checked = on;
      });
      root.querySelectorAll('[data-mn-pieces]').forEach((b) => {
        b.setAttribute('aria-pressed', String(st.tier === 'mini' && +b.getAttribute('data-mn-pieces') === st.pieces));
      });
      const mini = root.querySelector('[data-mn-set="mini"]');
      if (mini) {
        const v = V[`mini-${st.pieces}`] || {};
        const p = mini.querySelector('[data-mn-set-price]');
        if (p && v.money) {
          // DAILE Friday (assets/mn-dfriday.js) repaints from the base cents.
          if (p.hasAttribute('data-mn-price-cents')) p.setAttribute('data-mn-price-cents', String(v.cents || 0));
          p.textContent = decode(v.money);
        }
        const thumb = mini.querySelector('[data-mn-set-thumb]');
        const src = thumb && thumb.getAttribute(`data-src-${st.pieces}`);
        if (src && thumb.getAttribute('src') !== src) thumb.setAttribute('src', src);
        mini.classList.toggle('is-unavailable', !v.available);
      }
      // Mini size / time / pieces: the card (cm) and the "What's included" pane (cm / in, time).
      root.querySelectorAll('[data-mn-size]').forEach((el) => { el.textContent = el.getAttribute(`data-size-${st.pieces}`) || el.textContent; });
      root.querySelectorAll('[data-mn-time]').forEach((el) => { el.textContent = el.getAttribute(`data-time-${st.pieces}`) || el.textContent; });
      root.querySelectorAll('[data-mn-pcs]').forEach((el) => { el.textContent = String(st.pieces); });
      // Glue add-on row only while Mini is chosen; "What's included" shows the chosen set.
      const glueRow = root.querySelector('[data-mn-glue-row]');
      if (glueRow) glueRow.hidden = st.tier !== 'mini';
      root.querySelectorAll('[data-mn-inc-pane]').forEach((el) => { el.hidden = el.getAttribute('data-mn-inc-pane') !== st.tier; });
      root.querySelectorAll('[data-mn-inc-name]').forEach((el) => { el.hidden = el.getAttribute('data-mn-inc-name') !== st.tier; });
      syncForm();
      const price = st.priceText;
      const total = String(cents());
      setText('[data-mn-price], [data-mn-total], [data-mn-bar-price]', (el) => {
        if (el.hasAttribute('data-mn-price-cents')) el.setAttribute('data-mn-price-cents', total); // DAILE Friday base
        el.textContent = price;
      });
      const withCard = st.card ? `<span class="mn-pdp__sep" aria-hidden="true">·</span><span>${esc(label('cardWith', 'with postcard'))}</span>` : '';
      setText('[data-mn-price-note]', (el) => {
        let h = sumHtml(false);
        if (st.tier === 'mini') h += `<span class="mn-pdp__sep" aria-hidden="true">·</span><span>${esc(label(st.glue ? 'glueWith' : 'glueWithout'))}</span>`;
        el.innerHTML = h + withCard;
      });
      setText('[data-mn-sum]', (el) => { el.innerHTML = sumHtml(true) + withCard; });
      // Shipping line: free once the total reaches the threshold (only when it is shown, i.e. shop currency).
      d.querySelectorAll('[data-mn-ship]').forEach((box) => {
        // Conversion R1: the delivery price follows the chosen set (Mini parcels are lighter)
        const amt = box.querySelector('[data-mn-ship-amt]');
        if (amt) { const v = amt.getAttribute(st.tier === 'mini' ? 'data-mini' : 'data-big'); if (v) amt.textContent = v; }
        const thr = parseInt(box.getAttribute('data-thr'), 10) || 0;
        const free = box.querySelector('[data-mn-ship-free]');
        const paid = box.querySelector('[data-mn-ship-paid]');
        if (!thr || !free || !paid) return;
        const on = cents() >= thr;
        free.hidden = !on;
        paid.hidden = on;
      });
      setText('[data-mn-bar-sub]', (el) => { el.innerHTML = sumHtml(false); });
      if (Gallery) {
        const keys = st.tier === 'mini' ? ['mini', `mini-${st.pieces}`] : [st.tier];
        Gallery.filter(keys, st.tier);
      }
      try { d.dispatchEvent(new CustomEvent('daile-mvp:tier', { detail: st })); } catch (e) { /* */ }
    };

    cards.forEach((c) => {
      const r = c.querySelector('.mn-pdp-set__radio');
      if (!r) return;
      r.addEventListener('change', () => { if (r.checked) { st.tier = c.getAttribute('data-mn-set'); render(); } });
    });
    root.querySelectorAll('[data-mn-pieces]').forEach((b) => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        st.pieces = parseInt(b.getAttribute('data-mn-pieces'), 10) === 500 ? 500 : 300;
        st.tier = 'mini';
        render();
      });
    });
    if (glueInput) {
      glueInput.addEventListener('change', () => { st.glue = glueInput.checked; st.tier = 'mini'; render(); });
    }
    if (form) {
      form.addEventListener('submit', (e) => {
        syncForm();
        if (!st.available) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);
    }

    // Set photos (magnifier on each card): only that set's photos; does NOT select the set.
    const photosFor = (set) => {
      const keys = set === 'mini' ? ['mini', `mini-${st.pieces}`] : [set];
      const list = (cfg.setMedia || []).filter((m) => String(m.tags || '').split(',').some((t) => keys.indexOf(t.trim()) !== -1))
        .map((m) => ({ src: m.src, alt: decode(m.alt), w: m.w, h: m.h }));
      if (list.length) return list;
      const fb = (cfg.fallback || {})[set === 'mini' ? `mini-${st.pieces}` : set];
      return fb ? [{ src: fb.src, alt: '', w: fb.w, h: fb.h }] : [];
    };
    root.querySelectorAll('[data-mn-set-photos]').forEach((btn) => {
      ['pointerdown', 'mousedown', 'touchstart'].forEach((ev) => btn.addEventListener(ev, (e) => e.stopPropagation(), { passive: true }));
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!Lightbox) return;
        const set = btn.getAttribute('data-mn-set-photos');
        let name = label(`name.${set}`, set);
        if (set === 'mini') name += ` · ${st.pieces}`;
        Lightbox.open(photosFor(set), 0, { opener: btn, title: `${label('photos', 'Set photos')} — ${name}` });
      });
    });

    /* 11. Gift postcard ---------------------------------------------------- */
    const pc = root.querySelector('[data-mn-pc]');
    if (card && pc && cardInput) {
      const body = pc.querySelector('[data-mn-pc-body]');
      const input = pc.querySelector('[data-mn-pc-input]');
      const paper = pc.querySelector('[data-mn-pc-paper]');
      const textEl = pc.querySelector('[data-mn-pc-text]');
      const countEl = pc.querySelector('[data-mn-pc-count]');
      const errEl = pc.querySelector('[data-mn-pc-err]');
      const MAX = card.max || 300;
      const sample = label('cardSample', '').replace(/\|\|/g, '\n\n').replace(/\|/g, '\n');
      // Same text layout as the customizer / print (dv-cz-render.js layoutText + dv-customizer.js CARD_TEXT_STYLE):
      // flat 1600 px texture of a 16 × 22 card, 7 % padding, line height 1.28, ≤ 18 lines, size caps 0.1 w / 0.075 h,
      // binary-search shrink; each paragraph takes the direction of its first strong letter.
      const TW = 1600;
      const TH = Math.round(TW * 22 / 16);
      const FONT = '"Cormorant Garamond", Georgia, serif';
      const ctx = d.createElement('canvas').getContext('2d');
      const RTL = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
      const strong = (t) => { for (const ch of String(t)) { if (RTL.test(ch)) return 'rtl'; if (/\p{L}/u.test(ch)) return 'ltr'; } return null; };
      const layout = (message) => {
        const pad = 0.07; const iw = TW * (1 - 2 * pad); const ih = TH * (1 - 2 * pad); const lead = 1.28; const maxLines = 18;
        let ends = []; let dirs = [];
        const wrap = () => {
          const lines = []; ends = []; dirs = [];
          const push = (l, e, dr) => { lines.push(l); ends.push(e); dirs.push(dr); };
          String(message).split('\n').forEach((para) => {
            const dir = strong(para) || 'ltr';
            let line = '';
            para.trim().split(/\s+/).filter(Boolean).forEach((word) => {
              if (ctx.measureText(word).width > iw) {
                if (line) { push(line, false, dir); line = ''; }
                for (const ch of word) { if (ctx.measureText(line + ch).width > iw && line) { push(line, false, dir); line = ''; } line += ch; }
              } else if (line && ctx.measureText(`${line} ${word}`).width > iw) { push(line, false, dir); line = word; } else line += (line ? ' ' : '') + word;
            });
            push(line, true, dir);
          });
          return lines;
        };
        let lo = 1; let hi = Math.min(TW * 0.1, TH * 0.075);
        for (let i = 0; i < 18; i++) {
          const mid = (lo + hi) / 2;
          ctx.font = `300 ${mid}px ${FONT}`;
          const n = wrap().length;
          if (n * mid * lead <= ih && n <= maxLines) lo = mid; else hi = mid;
        }
        ctx.font = `300 ${lo}px ${FONT}`;
        const lines = wrap();
        return { ok: lines.length * lo * lead <= ih && lines.length <= maxLines, size: lo, lines, ends: ends.slice(), dirs: dirs.slice() };
      };
      let fit = { ok: true };
      let raf = 0;
      const paint = () => {
        raf = 0;
        const own = st.cardText.trim() !== '';
        fit = layout(own ? st.cardText : sample);
        if (!own) fit.ok = true;
        const w = paper ? paper.clientWidth : 0;
        if (textEl && w) {
          textEl.style.fontSize = `${(fit.size * w) / TW}px`;
          textEl.classList.toggle('is-sample', !own);
          textEl.innerHTML = fit.lines.map((l, i) => {
            const one = l.trim().split(/\s+/).length < 2;
            return `<span class="${fit.ends[i] || one ? 'is-end' : 'is-just'}" dir="${fit.dirs[i]}">${l ? esc(l) : '&nbsp;'}</span>`;
          }).join('');
        }
        if (countEl) countEl.textContent = label('charsLeft', '{n} characters left').replace('{n}', String(Math.max(0, MAX - st.cardText.length)));
        if (errEl) { errEl.hidden = fit.ok; errEl.textContent = fit.ok ? '' : label('cardLong', 'This message is too long for the card — please shorten it.'); }
        if (input) input.setAttribute('aria-invalid', String(!fit.ok));
      };
      const schedule = () => { if (!raf) raf = requestAnimationFrame(paint); };
      let fontAsked = false;
      const loadFont = () => {
        if (fontAsked) return;
        fontAsked = true;
        if (!d.querySelector('link[data-mn-pc-font]')) {
          const link = d.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300&display=swap';
          link.setAttribute('data-mn-pc-font', '');
          d.head.appendChild(link);
          link.addEventListener('load', () => {
            if (d.fonts && d.fonts.load) d.fonts.load(`300 40px ${FONT}`).then(schedule, schedule);
          });
        }
      };
      const toggle = () => {
        st.card = cardInput.checked;
        pc.classList.toggle('is-on', st.card);
        if (body) body.hidden = !st.card;
        if (st.card) { loadFont(); schedule(); }
        render();
      };
      cardInput.addEventListener('change', toggle);
      if (input) {
        input.addEventListener('input', () => {
          if (input.value.length > MAX) input.value = input.value.slice(0, MAX);
          st.cardText = input.value;
          schedule();
        });
      }
      if ('ResizeObserver' in window && paper) new ResizeObserver(() => { if (st.card) schedule(); }).observe(paper);
      if (input && input.value) st.cardText = input.value.slice(0, MAX);
      if (st.card) toggle();

      // Add to cart with the postcard: one request, children linked to the puzzle's _line_id (daile-cart.js uid format).
      // Runs before assets/daile-cart.js (window capture < document capture); without the postcard the form goes to
      // daile-cart.js handleAdd unchanged (form._daileMvpSelection).
      const say = (text) => {
        const msg = root.querySelector('[data-mn-buy-msg]');
        if (msg) { msg.textContent = text; msg.hidden = !text; }
      };
      let busy = false;
      const buttons = () => Array.from(form ? form.querySelectorAll('button[type="submit"]') : []).concat(Array.from(d.querySelectorAll('[data-mn-bar-add]')));
      window.addEventListener('submit', (e) => {
        if (!form || e.target !== form || !st.card) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (busy) return;
        syncForm();
        if (!st.available || !card.available) { say(label('unavailable')); return; }
        if (!fit.ok) { if (input) input.focus(); return; }
        const lineId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const items = [{ id: st.variantId, quantity: 1, properties: { _line_id: lineId } }];
        const sel = form._daileMvpSelection || {};
        if (sel.addonVariantId) items.push({ id: sel.addonVariantId, quantity: 1, properties: { _parent_line: lineId, _set: 'Glue Kit' } });
        items.push({ id: card.id, quantity: 1, properties: { _parent_line: lineId, 'Card message': st.cardText.trim() || '—' } });
        busy = true;
        say('');
        const btns = buttons();
        const texts = btns.map((b) => b.innerHTML);
        btns.forEach((b) => { b.disabled = true; b.textContent = label('adding', 'Adding…'); });
        form.setAttribute('aria-busy', 'true');
        fetch(`${shopRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        }).then((r) => r.json().then((j) => { if (!r.ok) throw j; return j; }))
          .then(() => {
            const C = window.DAILE_CART;
            if (C && C.refresh) return C.refresh().then(() => { if (C.open) C.open(); });
            window.location.href = `${shopRoot}cart`;
            return null;
          })
          .catch((err) => {
            say((err && err.description) || label('cartError', 'We couldn’t update your cart. Please try again.'));
            const C = window.DAILE_CART;
            if (C && C.refresh) C.refresh().catch(() => {});
          })
          .finally(() => {
            busy = false;
            form.removeAttribute('aria-busy');
            btns.forEach((b, k) => { b.innerHTML = texts[k]; });
            syncForm();
          });
      }, true);
    }

    render();
    return { state: st };
  })();

  /* 4. Sticky bars ========================================================== */

  (() => {
    if (!root || !hasIO) return;
    const cta = root.querySelector('[data-mn-main-cta]');
    const bar = root.querySelector('[data-mn-bar]');
    const top = root.querySelector('[data-mn-topbar]');
    const head = root.querySelector('.mn-pdp__head');
    const footer = d.querySelector('.mn-footer') || d.querySelector('footer');
    if (!cta) return;
    const form = root.querySelector('form[data-mn-form]');
    d.querySelectorAll('[data-mn-bar-add]').forEach((b) => b.addEventListener('click', () => {
      if (!form) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }));
    let ctaSeen = true; let ctaAbove = false; let headSeen = true; let footSeen = false;
    const update = () => {
      const showBar = !ctaSeen && !footSeen;
      if (bar) {
        bar.classList.toggle('is-visible', showBar);
        bar.querySelectorAll('a, button').forEach((b) => { b.tabIndex = showBar ? 0 : -1; });
      }
      const showTop = !ctaSeen && ctaAbove && !headSeen;
      if (top) {
        top.classList.toggle('is-visible', showTop);
        top.querySelectorAll('a, button').forEach((b) => { b.tabIndex = showTop ? 0 : -1; });
        html.classList.toggle('mn-pdp-topbar-on', showTop && window.matchMedia('(min-width: 990px)').matches);
      }
    };
    new IntersectionObserver(([en]) => {
      ctaSeen = en.isIntersecting;
      ctaAbove = !en.isIntersecting && en.boundingClientRect.top < 0;
      update();
    }, { rootMargin: '-60px 0px 0px 0px' }).observe(cta);
    if (head) new IntersectionObserver(([en]) => { headSeen = en.isIntersecting; update(); }).observe(head);
    if (footer) new IntersectionObserver(([en]) => { footSeen = en.isIntersecting; update(); }).observe(footer);
  })();

  /* 5. NFC dialog =========================================================== */

  (() => {
    const dlg = d.querySelector('[data-mn-nfc-dlg]');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const video = dlg.querySelector('video');
    let opener = null;
    const open = (from) => {
      opener = from;
      if (video) {
        if (video.dataset.poster && !video.getAttribute('poster')) video.setAttribute('poster', video.dataset.poster);
        if (video.dataset.src && !video.getAttribute('src')) video.setAttribute('src', video.dataset.src);
      }
      html.classList.add('mn-lock');
      dlg.showModal();
      if (video && !reduce.matches) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
    };
    d.addEventListener('click', (e) => {
      const o = e.target.closest('[data-mn-nfc-open]');
      if (o) { e.preventDefault(); open(o); }
    });
    dlg.addEventListener('click', (e) => {
      if (e.target.closest('[data-mn-dlg-close]')) { dlg.close(); return; }
      if (e.target === dlg) {
        const r = dlg.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
      }
    });
    dlg.addEventListener('close', () => {
      html.classList.remove('mn-lock');
      if (video) video.pause();
      if (opener && opener.focus) { try { opener.focus({ preventScroll: true }); } catch (e) { /* */ } }
    });
  })();

  /* 6. Remind me later (Klaviyo) =========================================== */

  (() => {
    const box = d.querySelector('[data-mn-remind]');
    if (!box) return;
    const company = box.getAttribute('data-kl-company');
    const listId = box.getAttribute('data-kl-list');
    const toggle = box.querySelector('[data-mn-remind-toggle]');
    const body = box.querySelector('.mn-pdp-remind__body');
    const emailEl = box.querySelector('[data-mn-remind-email]');
    const newsEl = box.querySelector('[data-mn-remind-news]');
    const saveEl = box.querySelector('[data-mn-remind-save]');
    const msgEl = box.querySelector('[data-mn-remind-msg]');
    const chips = Array.from(box.querySelectorAll('[data-mn-remind-in]'));
    let days = 7;
    if (toggle && body) {
      toggle.addEventListener('click', () => {
        const on = body.hidden;
        body.hidden = !on;
        box.classList.toggle('is-open', on);
        toggle.setAttribute('aria-expanded', String(on));
        if (on && emailEl) emailEl.focus({ preventScroll: true });
      });
    }
    chips.forEach((c) => c.addEventListener('click', () => {
      days = parseInt(c.getAttribute('data-mn-remind-in'), 10) || 7;
      chips.forEach((x) => x.setAttribute('aria-pressed', String(x === c)));
    }));
    const say = (text, bad) => { if (!msgEl) return; msgEl.textContent = text; msgEl.classList.toggle('is-bad', !!bad); msgEl.hidden = false; };
    const due = () => {
      const dt = new Date(Date.now() + days * 864e5);
      const p2 = (n) => (n < 10 ? '0' : '') + n;
      return `${dt.getFullYear()}-${p2(dt.getMonth() + 1)}-${p2(dt.getDate())}`;
    };
    const nice = (iso) => {
      try { return new Date(`${iso}T12:00:00`).toLocaleDateString(html.lang || undefined, { day: 'numeric', month: 'long', numberingSystem: 'latn' }); } catch (e) { return iso; }
    };
    const props = (dueDate) => {
      const st = window.DAILE_MVP || {};
      const h1 = d.querySelector('.mn-pdp__title, h1');
      return {
        reminder_due: dueDate,
        reminder_product: h1 ? h1.textContent.trim() : d.title,
        reminder_url: window.location.origin + window.location.pathname,
        reminder_set: st.tier || '',
        reminder_pieces: st.pieces || '',
        reminder_price: st.priceText || '',
        daile_newsletter_opt_in: !!(newsEl && newsEl.checked),
      };
    };
    const klaviyo = (path, payload) => fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(payload),
    });
    if (saveEl) {
      saveEl.addEventListener('click', () => {
        const email = ((emailEl && emailEl.value) || '').trim();
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)) { say(box.getAttribute('data-msg-bad'), true); if (emailEl) emailEl.focus(); return; }
        if (!company || !listId) { say(box.getAttribute('data-msg-err'), true); return; }
        const dueDate = due();
        const p = props(dueDate);
        saveEl.disabled = true;
        klaviyo('subscriptions', {
          data: {
            type: 'subscription',
            attributes: { custom_source: 'PDP reminder', profile: { data: { type: 'profile', attributes: { email, properties: p } } } },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }).then((res) => {
          if (!res.ok) throw new Error(`klaviyo ${res.status}`);
          klaviyo('events', {
            data: {
              type: 'event',
              attributes: {
                properties: p,
                metric: { data: { type: 'metric', attributes: { name: 'Requested Reminder' } } },
                profile: { data: { type: 'profile', attributes: { email } } },
              },
            },
          }).catch(() => {});
          box.classList.add('is-done');
          say(`${box.getAttribute('data-msg-ok')} ${nice(dueDate)}.`);
        }).catch(() => { saveEl.disabled = false; say(box.getAttribute('data-msg-err'), true); });
      });
    }
    if (emailEl) emailEl.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); saveEl.click(); } });
  })();

  /* 7. Custom puzzle: upload a photo straight from "How it works" ============ */

  (() => {
    const input = d.querySelector('[data-mn-upload]');
    if (!input) return;
    const zone = d.querySelector('[data-mn-upload-zone]');
    const errEl = d.querySelector('[data-mn-upload-error]');
    const save = (file) => new Promise((resolve, reject) => {
      let rq;
      try { rq = window.indexedDB.open('daile-cz', 1); } catch (e) { reject(e); return; }
      rq.onupgradeneeded = () => { rq.result.createObjectStore('photos'); };
      rq.onerror = () => reject(rq.error);
      rq.onsuccess = () => {
        const db = rq.result;
        const put = (val, onFail) => {
          try {
            const tx = db.transaction('photos', 'readwrite');
            tx.objectStore('photos').put(val, 'photo_pdp-pending');
            tx.oncomplete = () => resolve();
            tx.onerror = () => onFail(tx.error);
          } catch (e) { onFail(e); }
        };
        put(file, () => {
          const fr = new FileReader();
          fr.onload = () => put({ buf: fr.result, type: file.type }, reject);
          fr.onerror = () => reject(fr.error);
          fr.readAsArrayBuffer(file);
        });
      };
    });
    const fail = (attr, fallback) => { if (errEl) { errEl.textContent = errEl.getAttribute(attr) || fallback; errEl.hidden = false; } input.value = ''; };
    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (!file) return;
      if (file.type !== 'image/jpeg' && file.type !== 'image/png') { fail('data-msg-type', 'Please upload a JPG or PNG photo.'); return; }
      if (file.size > 60 * 1024 * 1024) { fail('data-msg-size', 'This photo is over 60 MB — please use a smaller file (JPG works best).'); return; }
      if (errEl) errEl.hidden = true;
      if (zone) zone.classList.add('is-busy');
      const st = window.DAILE_MVP || {};
      const setQ = st.czSet ? `set=${encodeURIComponent(st.czSet)}${st.czGlue ? '&glue=1' : ''}` : '';
      save(file)
        .then(() => { window.location.href = `${shopRoot}pages/customizer?pdp=1${setQ ? `&${setQ}` : ''}`; })
        .catch(() => { window.location.href = `${shopRoot}pages/customizer${setQ ? `?${setQ}` : ''}`; });
    });
    window.addEventListener('pageshow', () => { if (zone) zone.classList.remove('is-busy'); try { input.value = ''; } catch (e) { /* */ } });
  })();

  /* 8. "What's in the box" follows the set ================================ */

  d.querySelectorAll('[data-mn-box]').forEach((box) => {
    const tabs = Array.from(box.querySelectorAll('[data-mn-box-tab]'));
    const panes = Array.from(box.querySelectorAll('[data-mn-box-pane]'));
    const show = (set, focus) => {
      tabs.forEach((t) => {
        const on = t.getAttribute('data-mn-box-tab') === set;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panes.forEach((p) => { p.hidden = p.getAttribute('data-mn-box-pane') !== set; });
    };
    tabs.forEach((t, k) => {
      t.addEventListener('click', () => show(t.getAttribute('data-mn-box-tab')));
      t.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        let fwd = e.key === 'ArrowRight';
        if (isRtl()) fwd = !fwd;
        const n = tabs[(k + (fwd ? 1 : -1) + tabs.length) % tabs.length];
        show(n.getAttribute('data-mn-box-tab'), true);
      });
    });
    const sync = (st) => {
      if (!st || !st.tier) return;
      show(st.tier);
      box.querySelectorAll('[data-mn-box-pieces]').forEach((el) => { el.textContent = String(st.pieces === 500 ? 500 : 300); });
      box.querySelectorAll('[data-mn-box-size]').forEach((el) => { el.textContent = el.getAttribute(`data-size-${st.pieces === 500 ? 500 : 300}`) || el.textContent; });
    };
    d.addEventListener('daile-mvp:tier', (e) => sync(e.detail));
    if (Buy) sync(Buy.state);
  });

  /* 9. Related products (recommendations API) ============================== */

  d.querySelectorAll('[data-mn-related]').forEach((sec) => {
    const url = sec.getAttribute('data-url');
    if (!url) return;
    let done = false;
    const load = () => {
      if (done) return;
      done = true;
      fetch(url).then((r) => r.text()).then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-mn-related]');
        // The shell holds placeholder cards (reserved height, R7 CLS fix) — real cards sit in a rail track.
        if (!fresh || !fresh.querySelector('[data-mn-rail-track] .mn-art')) { if (!sec.querySelector('[data-mn-rail-track] .mn-art')) sec.hidden = true; return; }
        sec.innerHTML = fresh.innerHTML;
        sec.classList.remove('mn-pdp-related--wait');
        sec.hidden = false;
        previewLinks(sec);
        sec.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
      }).catch(() => { if (!sec.querySelector('[data-mn-rail-track] .mn-art')) sec.hidden = true; });
    };
    if (!hasIO) { load(); return; }
    const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '1200px 0px' });
    io.observe(sec);
  });

  /* 12. R9 shipping line "Change" → the shared country / language modal =====
     window.mnLocale.open() (built by the header group); while it is missing (no modal script, or not loaded yet)
     the inline country select stays — re-checked on window load and on every click. */
  d.querySelectorAll('[data-mn-ship]').forEach((box) => {
    const btn = box.querySelector('[data-mn-ship-change]');
    const form = box.querySelector('.mn-pdp-ship__form');
    if (!btn || !form) return;
    const modal = () => !!(window.mnLocale && typeof window.mnLocale.open === 'function');
    const sync = () => {
      const on = modal();
      btn.hidden = !on;
      form.hidden = on;
    };
    btn.addEventListener('click', () => {
      if (modal()) {
        try { window.mnLocale.open(btn); return; } catch (e) { /* fall back to the select */ }
      }
      btn.hidden = true;
      form.hidden = false;
      const sel = form.querySelector('select');
      if (!sel) return;
      sel.focus({ preventScroll: true });
      try { if (typeof sel.showPicker === 'function') sel.showPicker(); } catch (e) { /* */ }
    });
    sync();
    if (d.readyState !== 'complete') window.addEventListener('load', sync, { once: true });
    d.addEventListener('mn:locale-ready', sync);
  });

  /* 10. Preview links + cookie banner offset =============================== */

  // While the owner previews ?view=mono, keep product links inside the Mono preview.
  function previewLinks(scope) {
    let view = '';
    try { view = new URLSearchParams(window.location.search).get('view') || ''; } catch (e) { return; }
    if (view.indexOf('mono') !== 0) return;
    (scope || d).querySelectorAll('a[href*="/products/"]').forEach((a) => {
      if (a.closest('[data-mn-pdp-skip-preview]')) return;
      try {
        const u = new URL(a.getAttribute('href'), window.location.href);
        if (u.origin !== window.location.origin || u.searchParams.get('view')) return;
        const custom = /\/products\/custom-puzzle\/?$/.test(u.pathname);
        u.searchParams.set('view', custom ? 'mono-custom' : 'mono');
        a.setAttribute('href', u.pathname + u.search + u.hash);
      } catch (e) { /* */ }
    });
  }
  previewLinks(d.querySelector('main') || d);

  (() => {
    const box = d.querySelector('[data-daile-cookie]');
    if (!box || !('ResizeObserver' in window)) return;
    const set = () => {
      const visible = !box.hidden && box.offsetHeight > 0 && getComputedStyle(box).position === 'fixed';
      html.style.setProperty('--mn-cookie-h', visible ? `${box.offsetHeight}px` : '0px');
    };
    new ResizeObserver(set).observe(box);
    new MutationObserver(set).observe(box, { attributes: true, attributeFilter: ['hidden', 'class'] });
    set();
  })();
})();
